import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useState } from 'react';
import addDays from 'date-fns/addDays';
import { addHours } from 'date-fns';
import {
  calcContractItemRecurringPrice, calcIntervalFraction, IContractStateType, CreateHeaders, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, getAllItemsOnContract,
  GrayOutlinedButton, HeaderSizeEnum, PrimaryButton, SnackBarContext, textHeader, toCurrency, IContractItem
} from '@frontend-workspace/react-shared-lib';
import { IMoveOrder } from '../_models/move-order';
import WebkuaTable from '../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../requestUtils';

/* eslint-disable-next-line */
export interface MoveContractInvoiceDialogProps {
  onClose: () => void,
  contract: IContractStateType
}

export function MoveContractInvoiceDialog(props: MoveContractInvoiceDialogProps) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  const currentInvoiceDate = new Date(props.contract.contractInfo.nextInvoicingDate);
  const [newInvoiceDate, setNewInvoiceDate] = useState<Date>(addDays(currentInvoiceDate, 7));

  const moveCostBeforeFraction = getAllItemsOnContract(props.contract).reduce((sum: number, o: IContractItem) => sum + calcContractItemRecurringPrice(o), 0);
  const fraction = calcIntervalFraction(props.contract.contractInfo, currentInvoiceDate, newInvoiceDate);
  const moveCost = fraction * moveCostBeforeFraction;

  const moveOrder: IMoveOrder = {
    newInvoiceDate,
    contractNumber: props.contract.contractNumber,
    createSale: true,
    moveCost
  };

  const handleSetNewInvoiceDate = (date: Date) => date && setNewInvoiceDate(addHours(date, 3));

  const handleMoveContract = () => {
    makeRequest(`contracts/action/moveInvoice`, 'PUT', moveOrder).then(result => {
      setSnackbarMessage({ content: t("moveContractInvoice.changeInvoiceDate"), type: 'info' });
      props.onClose();
    });
  };

  const moveCostRenderer = (params: ValueFormatterParams) => {
    return (
      toCurrency(
        fraction * calcContractItemRecurringPrice(params.data),
        props.contract.contractInfo.currency
      )
    );
  };

  const headers = CreateHeaders([
    textHeader("productName", t("standard.name"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("recurringDiscountPercent", t("standard.recurringDiscountPercent"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("moveCost", t("moveContractInvoice.moveCost"), HeaderSizeEnum.MEDIUM, {
      renderer: moveCostRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return (
    <Dialog
      open={true}
      maxWidth="xl"
      fullWidth>
      <DialogTitle>
        {t("moveContractInvoice.moveInvoiceDate")}
      </DialogTitle>
      <DialogContent>
        <div className='mb-3 mb-md-0'>
          <DatePicker
            inputFormat="dd.MM.yyyy"
            label={t("moveContractInvoice.newInvoiceDate")}
            value={newInvoiceDate}
            onChange={handleSetNewInvoiceDate}
          />
        </div>
        <WebkuaTable
          headers={headers}
          data={getAllItemsOnContract(props.contract)}
        />
        <div className='mt-2'>Sum: {toCurrency(moveCost, props.contract.contractInfo.currency)}</div>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant='contained'
          onClick={handleMoveContract}
          size='large'
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          onClick={props.onClose}
          size='large'
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default MoveContractInvoiceDialog;